<template>
  <div class="subscription-create">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <v-btn
                icon
                @click="$router.go(-1)"
                class="mr-3"
                color="primary"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-plus-circle</v-icon>
                Create Subscription Plan
                <span class="page-inline-subtitle">Design a new subscription plan</span>
              </h1>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Form -->
      <v-card class="subscription-form-card" elevation="8">
        <v-card-title class="form-header">
          <v-icon left>mdi-package-variant-plus</v-icon>
          New Subscription Plan
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <v-row>
              <!-- Basic Information -->
              <v-col cols="12">
                <h3 class="section-title">Basic Information</h3>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  outlined
                  label="Plan Name *"
                  prepend-inner-icon="mdi-package-variant"
                  :rules="[v => !!v || 'Plan name is required']"
                  required
                  class="minimal-input"
                  dense
                  placeholder="e.g., Professional Plan"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="form.type"
                  outlined
                  label="Plan Type *"
                  :items="planTypes"
                  prepend-inner-icon="mdi-tag"
                  :rules="[v => !!v || 'Plan type is required']"
                  required
                  class="minimal-input"
                  dense
                ></v-select>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.description"
                  outlined
                  label="Description"
                  prepend-inner-icon="mdi-text"
                  rows="3"
                  class="minimal-input"
                  dense
                  placeholder="Describe what makes this plan special..."
                ></v-textarea>
              </v-col>

              <!-- Pricing -->
              <v-col cols="12">
                <h3 class="section-title">Pricing</h3>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="form.monthly_price"
                  outlined
                  label="Monthly Price *"
                  prepend-inner-icon="mdi-currency-usd"
                  type="number"
                  min="0"
                  step="0.01"
                  :rules="[v => v >= 0 || 'Price must be positive']"
                  required
                  class="minimal-input"
                  dense
                  placeholder="0.00"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="form.annual_price"
                  outlined
                  label="Annual Price *"
                  prepend-inner-icon="mdi-currency-usd"
                  type="number"
                  min="0"
                  step="0.01"
                  :rules="[v => v >= 0 || 'Price must be positive']"
                  required
                  class="minimal-input"
                  dense
                  placeholder="0.00"
                  :hint="annualSavingsHint"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="form.setup_fee"
                  outlined
                  label="Setup Fee"
                  prepend-inner-icon="mdi-currency-usd"
                  type="number"
                  min="0"
                  step="0.01"
                  class="minimal-input"
                  dense
                  placeholder="0.00"
                  hint="One-time setup fee (optional)"
                ></v-text-field>
              </v-col>

              <!-- Usage Limits -->
              <v-col cols="12">
                <h3 class="section-title">Usage Limits</h3>
                <p class="section-subtitle">Leave empty for unlimited</p>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="form.max_users"
                  outlined
                  label="Max Users"
                  prepend-inner-icon="mdi-account-group"
                  type="number"
                  min="1"
                  class="minimal-input"
                  dense
                  placeholder="Unlimited"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="form.max_projects"
                  outlined
                  label="Max Projects"
                  prepend-inner-icon="mdi-folder"
                  type="number"
                  min="1"
                  class="minimal-input"
                  dense
                  placeholder="Unlimited"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="form.max_storage_gb"
                  outlined
                  label="Max Storage (GB)"
                  prepend-inner-icon="mdi-harddisk"
                  type="number"
                  min="1"
                  class="minimal-input"
                  dense
                  placeholder="Unlimited"
                ></v-text-field>
              </v-col>

              <!-- Settings -->
              <v-col cols="12">
                <h3 class="section-title">Settings</h3>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="form.support_level"
                  outlined
                  label="Support Level *"
                  :items="supportLevels"
                  prepend-inner-icon="mdi-help-circle"
                  :rules="[v => !!v || 'Support level is required']"
                  required
                  class="minimal-input"
                  dense
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-4 mt-2">
                  <v-switch
                    v-model="form.is_popular"
                    inset
                    color="primary"
                    label="Mark as Popular"
                    class="minimal-switch"
                    hide-details
                  ></v-switch>
                  <v-switch
                    v-model="form.is_active"
                    inset
                    color="primary"
                    label="Active"
                    class="minimal-switch"
                    hide-details
                  ></v-switch>
                </div>
              </v-col>

              <!-- Features -->
              <v-col cols="12">
                <h3 class="section-title">Features</h3>
                <p class="section-subtitle">Add features that are included in this plan</p>
                <div class="features-section">
                  <div 
                    v-for="(feature, index) in form.features" 
                    :key="index"
                    class="feature-item"
                  >
                    <v-text-field
                      v-model="feature.name"
                      outlined
                      label="Feature Name"
                      prepend-inner-icon="mdi-check-circle"
                      class="minimal-input feature-name"
                      dense
                      placeholder="e.g., 24/7 Support"
                    ></v-text-field>
                    <v-text-field
                      v-model="feature.description"
                      outlined
                      label="Feature Description"
                      class="minimal-input feature-description"
                      dense
                      placeholder="Brief description of this feature"
                    ></v-text-field>
                    <v-switch
                      v-model="feature.is_active"
                      inset
                      color="primary"
                      label="Included"
                      class="minimal-switch feature-switch"
                      hide-details
                    ></v-switch>
                    <v-btn
                      icon
                      color="error"
                      @click="removeFeature(index)"
                      class="feature-remove"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                  
                  <v-btn
                    outlined
                    color="primary"
                    @click="addFeature"
                    class="add-feature-btn"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add Feature
                  </v-btn>
                </div>
              </v-col>

              <!-- AI Agents Selection -->
              <v-col cols="12">
                <h3 class="section-title">AI Agents</h3>
                <p class="section-subtitle">Select AI agents to include in this plan</p>
                <div class="agents-section">
                  <div v-if="availableAgents.length === 0" class="no-agents-message">
                    <v-icon large color="grey">mdi-robot-confused</v-icon>
                    <p>No AI agents available. <router-link to="/ai-agents">Create an AI agent first</router-link>.</p>
                  </div>
                  <div v-else class="agents-grid">
                    <div
                      v-for="agent in availableAgents"
                      :key="agent.id"
                      class="agent-select-card"
                      :class="{ 'selected': isAgentSelected(agent.id) }"
                      @click="toggleAgent(agent)"
                    >
                      <v-checkbox
                        :value="isAgentSelected(agent.id)"
                        @click.stop="toggleAgent(agent)"
                        color="primary"
                        hide-details
                        class="agent-checkbox"
                      ></v-checkbox>
                      <v-avatar size="32" class="mb-2">
                        <v-img
                          :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                          :alt="agent.name"
                        />
                      </v-avatar>
                      <div class="agent-name">{{ agent.name }}</div>
                      <div class="agent-price">${{ agent.monthly_price || 0 }}/mo</div>
                      
                      <!-- Agent configuration (shown when selected) -->
                      <div v-if="isAgentSelected(agent.id)" class="agent-config" @click.stop>
                        <v-text-field
                          v-model.number="getAgentConfig(agent.id).included_credits"
                          outlined
                          label="Included Credits"
                          type="number"
                          min="0"
                          dense
                          class="config-input"
                          placeholder="Unlimited"
                        ></v-text-field>
                        <v-text-field
                          v-model.number="getAgentConfig(agent.id).overage_rate"
                          outlined
                          label="Overage Rate"
                          type="number"
                          min="0"
                          step="0.001"
                          dense
                          class="config-input"
                          placeholder="0.000"
                        ></v-text-field>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="form-actions pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="$router.go(-1)"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="createSubscription"
            :loading="isCreating"
            :disabled="!valid"
            class="save-btn"
          >
            Create Plan
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SubscriptionCreate",
  data() {
    return {
      valid: false,
      isCreating: false,
      availableAgents: [],
      form: {
        name: '',
        description: '',
        type: '',
        monthly_price: 0,
        annual_price: 0,
        setup_fee: 0,
        max_users: null,
        max_projects: null,
        max_storage_gb: null,
        support_level: '',
        is_popular: false,
        is_active: true,
        features: [],
        agents: []
      },
      planTypes: [
        { text: 'Starter', value: 'starter' },
        { text: 'Professional', value: 'professional' },
        { text: 'Enterprise', value: 'enterprise' },
        { text: 'Custom', value: 'custom' }
      ],
      supportLevels: [
        { text: 'Email Support', value: 'email' },
        { text: 'Priority Support', value: 'priority' },
        { text: 'Dedicated Support', value: 'dedicated' }
      ]
    };
  },
  computed: {
    annualSavingsHint() {
      if (this.form.monthly_price && this.form.annual_price) {
        const monthlyTotal = this.form.monthly_price * 12;
        const savings = monthlyTotal - this.form.annual_price;
        if (savings > 0) {
          return `Save $${savings.toFixed(2)} annually`;
        }
      }
      return '';
    }
  },
  mounted() {
    this.loadAvailableAgents();
    this.addDefaultFeatures();
  },
  methods: {
    async loadAvailableAgents() {
      try {
        const response = await axios.get('/ai-agents?is_active=true');
        this.availableAgents = response.data.data;
      } catch (error) {
        console.error('Error loading agents:', error);
      }
    },
    addDefaultFeatures() {
      // Add some common features as starting points
      this.form.features = [
        { name: 'Email Support', description: '24/7 email support', is_active: true },
        { name: 'Basic Analytics', description: 'View usage statistics', is_active: true },
        { name: 'API Access', description: 'RESTful API access', is_active: true },
      ];
    },
    async createSubscription() {
      if (!this.$refs.form.validate()) return;
      
      this.isCreating = true;
      try {
        await axios.post('/subscription-plans', this.form);
        this.$router.push('/subscriptions');
      } catch (error) {
        console.error('Error creating subscription:', error);
      }
      this.isCreating = false;
    },
    addFeature() {
      this.form.features.push({
        name: '',
        description: '',
        is_active: true
      });
    },
    removeFeature(index) {
      this.form.features.splice(index, 1);
    },
    isAgentSelected(agentId) {
      return this.form.agents.some(a => a.ai_agent_id === agentId);
    },
    toggleAgent(agent) {
      const index = this.form.agents.findIndex(a => a.ai_agent_id === agent.id);
      if (index > -1) {
        this.form.agents.splice(index, 1);
      } else {
        this.form.agents.push({
          ai_agent_id: agent.id,
          included_credits: null,
          overage_rate: 0
        });
      }
    },
    getAgentConfig(agentId) {
      const config = this.form.agents.find(a => a.ai_agent_id === agentId);
      return config || { included_credits: null, overage_rate: 0 };
    }
  }
};
</script>

<style scoped>
.subscription-create {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4));
  font-size: 32px !important;
}

/* Form Card */
.subscription-form-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 20px 24px !important;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e2e8f0;
}

.section-subtitle {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 16px;
  margin-top: -4px;
}

/* Features Section */
.features-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #f8f9fa;
}

.feature-item {
  display: grid;
  grid-template-columns: 1fr 1fr auto auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.feature-name {
  grid-column: 1;
}

.feature-description {
  grid-column: 2;
}

.feature-switch {
  grid-column: 3;
  margin: 0;
}

.feature-remove {
  grid-column: 4;
  margin: 0;
}

.add-feature-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

/* Agents Section */
.agents-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #f8f9fa;
}

.no-agents-message {
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
}

.no-agents-message p {
  margin: 12px 0 0 0;
  font-size: 16px;
}

.no-agents-message a {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.agent-select-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.agent-select-card:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.agent-select-card.selected {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.agent-checkbox {
  position: absolute;
  top: 8px;
  right: 8px;
}

.agent-name {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.agent-price {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 12px;
}

.agent-config {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.config-input {
  margin-bottom: 0 !important;
}

.config-input >>> .v-input__control {
  min-height: 40px !important;
}

.config-input >>> .v-input__control .v-input__slot {
  min-height: 40px !important;
  padding: 0 12px !important;
}

.config-input >>> input {
  font-size: 12px !important;
}

.config-input >>> .v-label {
  font-size: 12px !important;
}

/* Minimal Input Styling */
.minimal-input {
  margin-bottom: 8px;
}

.minimal-input >>> .v-input__control {
  min-height: 56px;
}

.minimal-input >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  padding: 0 16px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:before,
.minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-label {
  color: #64748b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-label--active {
  color: #667eea !important;
  transform: translateY(-18px) scale(0.85) !important;
}

.minimal-input >>> input,
.minimal-input >>> textarea {
  color: #1e293b !important;
  font-weight: 500;
  font-size: 15px;
  padding: 8px 0 !important;
}

.minimal-input >>> .v-input__prepend-inner .v-icon {
  color: #667eea !important;
  margin-right: 12px;
}

/* Remove fieldset borders */
.minimal-input >>> fieldset {
  display: none !important;
}

/* Switch Styling */
.minimal-switch >>> .v-input--switch__thumb {
  color: #667eea !important;
}

.minimal-switch >>> .v-input--switch__track {
  color: #e2e8f0 !important;
}

.minimal-switch >>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: #667eea !important;
}

.minimal-switch >>> .v-label {
  color: #2c3e50 !important;
  font-weight: 500;
  font-size: 15px;
}

/* Form Actions */
.form-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

.save-btn {
  background: #667eea !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.save-btn:hover {
  background: #5a6fd8 !important;
}

.save-btn:before {
  background-color: transparent;
}

/* Responsive Design */
@media (max-width: 960px) {
  .feature-item {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .feature-name,
  .feature-description,
  .feature-switch,
  .feature-remove {
    grid-column: 1;
  }
  
  .feature-switch,
  .feature-remove {
    justify-self: start;
  }
  
  .agents-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 20px;
  }
  
  .features-section,
  .agents-section {
    padding: 12px;
  }
  
  .feature-item {
    padding: 8px;
  }
  
  .agents-grid {
    grid-template-columns: 1fr;
  }
}
</style>